import { parseLaborPaysBy, type LaborPaysBy } from "@/lib/manufacturing/laborPayQuantity";
import { parseBulkImport } from "@/lib/manufacturing/bulkImportParser";
import { normalizeLaborMatchKey } from "@/lib/manufacturing/laborCompanyPayMatch";
import {
  findRecipeForLaborMatchKey,
  type LaborRateRecipePick,
} from "@/lib/manufacturing/laborCompanyRateRecipe";

export const LABOR_RATE_IMPORT_HEADERS = ["sku", "name", "rate_usd", "label", "notes", "pays_by"] as const;

export type LaborRateImportStatus = "suggested" | "unmatched" | "rejected" | "accepted";

export type LaborRateImportRow = {
  id: string;
  sku: string;
  name: string;
  label: string;
  notes: string;
  pays_by: LaborPaysBy | null;
  rate_raw: string;
  rate_usd: number | null;
  recipe: LaborRateRecipePick | null;
  existing_rate_id: string | null;
  status: LaborRateImportStatus;
  reason: string;
};

export type LaborRateImportExisting = {
  id: string;
  recipe_id: string | null;
  item_code: string;
};

const SKU_KEYS = ["sku", "item_code", "code", "recipe_code", "option_code", "match_key"];
const NAME_KEYS = ["name", "recipe_name"];
const RATE_KEYS = ["rate_usd", "rate", "flat_rate_usd", "amount", "pay"];
const LABEL_KEYS = ["label"];
const NOTES_KEYS = ["notes"];
const PAYS_BY_KEYS = ["pays_by", "pay_by", "count_from", "quantity_basis"];

function cell(row: Record<string, unknown>, keys: readonly string[]): string {
  const map = new Map(Object.entries(row).map(([k, v]) => [k.trim().toLowerCase(), v]));
  for (const key of keys) {
    const v = map.get(key);
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
}

export function parseLaborRateUsd(raw: string): number | null {
  const cleaned = raw.replace(/[$,]/g, "").trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100) / 100;
}

function pickExisting(
  recipe: LaborRateRecipePick | null,
  sku: string,
  existing: readonly LaborRateImportExisting[]
): string | null {
  if (recipe) {
    const byRecipe = existing.find((e) => e.recipe_id === recipe.id);
    if (byRecipe) return byRecipe.id;
    const byCode = existing.find((e) => normalizeLaborMatchKey(e.item_code) === normalizeLaborMatchKey(recipe.code));
    if (byCode) return byCode.id;
  }
  if (sku) {
    const bySku = existing.find((e) => normalizeLaborMatchKey(e.item_code) === normalizeLaborMatchKey(sku));
    if (bySku) return bySku.id;
  }
  return null;
}

export function matchLaborRateImportRow(
  input: { sku: string; name: string; label: string; notes: string; rate_raw: string; pays_by: string },
  recipes: readonly LaborRateRecipePick[],
  existing: readonly LaborRateImportExisting[],
  id: string
): LaborRateImportRow {
  const rate_usd = parseLaborRateUsd(input.rate_raw);
  const pays_by = parseLaborPaysBy(input.pays_by);
  const bySku = input.sku ? findRecipeForLaborMatchKey(recipes, input.sku) : null;
  const byName = !bySku && input.name ? findRecipeForLaborMatchKey(recipes, input.name) : null;
  const recipe = bySku ?? byName;
  let status: LaborRateImportStatus = "suggested";
  let reason = "";
  if (!input.sku && !input.name) {
    status = "unmatched";
    reason = "missing_sku";
  } else if (!recipe) {
    status = "unmatched";
    reason = "no_match";
  } else if (rate_usd == null) {
    status = "unmatched";
    reason = "missing_rate";
  }
  return {
    id,
    sku: input.sku,
    name: input.name,
    label: input.label,
    notes: input.notes,
    pays_by,
    rate_raw: input.rate_raw,
    rate_usd,
    recipe,
    existing_rate_id: pickExisting(recipe, input.sku, existing),
    status,
    reason,
  };
}

export function parseLaborRateImportText(
  text: string,
  recipes: readonly LaborRateRecipePick[],
  existing: readonly LaborRateImportExisting[] = []
): { ok: true; rows: LaborRateImportRow[] } | { ok: false; error: string } {
  const parsed = parseBulkImport(text);
  if (!parsed.ok) return parsed;
  const rows = parsed.rows.map((row, i) =>
    matchLaborRateImportRow(
      {
        sku: cell(row, SKU_KEYS),
        name: cell(row, NAME_KEYS),
        label: cell(row, LABEL_KEYS),
        notes: cell(row, NOTES_KEYS),
        pays_by: cell(row, PAYS_BY_KEYS),
        rate_raw: cell(row, RATE_KEYS),
      },
      recipes,
      existing,
      `row-${i + 1}`
    )
  );
  if (rows.length === 0) return { ok: false, error: "No data rows found." };
  return { ok: true, rows };
}

function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCsv(headers: readonly string[], rows: Array<Array<unknown>>): string {
  return [headers.join(","), ...rows.map((row) => row.map(csvCell).join(","))].join("\n") + "\n";
}

export function laborRateImportTemplateCsv(
  recipes: readonly LaborRateRecipePick[],
  existing: readonly {
    recipe_id: string | null;
    item_code: string;
    flat_rate_usd: number;
    label: string;
    notes: string;
    pays_by?: LaborPaysBy | null;
  }[] = []
): string {
  const rows = recipes.map((r) => {
    const hit =
      existing.find((e) => e.recipe_id === r.id) ??
      existing.find((e) => normalizeLaborMatchKey(e.item_code) === normalizeLaborMatchKey(r.code));
    return [
      r.code,
      r.name,
      hit ? hit.flat_rate_usd : "",
      hit?.label && hit.label !== r.name ? hit.label : "",
      hit?.notes ?? "",
      hit?.pays_by ?? "",
    ];
  });
  return toCsv(LABOR_RATE_IMPORT_HEADERS, rows);
}

export function laborRateImportUnmatchedItemCode(row: LaborRateImportRow): string {
  return (
    row.sku ||
    row.recipe?.code ||
    normalizeLaborMatchKey(row.name) ||
    normalizeLaborMatchKey(row.label)
  ).trim();
}

function canParkImportRow(row: LaborRateImportRow): row is LaborRateImportRow & { rate_usd: number } {
  return row.rate_usd != null && Boolean(laborRateImportUnmatchedItemCode(row));
}

/** Unmatched, rejected, and optionally leftover suggested rows that can park as unmatched pay items. */
export function laborRateImportParkableRows(
  rows: readonly LaborRateImportRow[],
  includeSuggestedLeftovers = false
): Array<LaborRateImportRow & { rate_usd: number }> {
  return rows.filter((r): r is LaborRateImportRow & { rate_usd: number } => {
    if (r.status === "accepted" || !canParkImportRow(r)) return false;
    if (r.status === "unmatched" || r.status === "rejected") return true;
    return includeSuggestedLeftovers && r.status === "suggested";
  });
}

/** Unmatched import rows that can be parked as pay items with no recipe yet. */
export function laborRateImportUnmatchedToSave(
  rows: readonly LaborRateImportRow[]
): Array<LaborRateImportRow & { rate_usd: number }> {
  return laborRateImportParkableRows(rows, false);
}

export function laborRateImportLeftoverRows(
  rows: readonly LaborRateImportRow[],
  includeSuggestedParked = false
): LaborRateImportRow[] {
  const parked = new Set(laborRateImportParkableRows(rows, includeSuggestedParked).map((r) => r.id));
  return rows.filter((r) => r.status !== "accepted" && !parked.has(r.id));
}

export function laborRateImportLeftoverCsv(
  rows: readonly LaborRateImportRow[],
  includeSuggestedParked = false
): string {
  const leftover = laborRateImportLeftoverRows(rows, includeSuggestedParked);
  return toCsv(
    [...LABOR_RATE_IMPORT_HEADERS, "status", "reason"],
    leftover.map((r) => [
      r.sku || r.recipe?.code || "",
      r.name || r.recipe?.name || "",
      r.rate_usd ?? r.rate_raw,
      r.label,
      r.notes,
      r.pays_by ?? "",
      r.status,
      r.reason || (r.status === "suggested" ? "not_accepted" : r.status),
    ])
  );
}

export function laborRateImportCounts(rows: readonly LaborRateImportRow[]) {
  const leftover = laborRateImportLeftoverRows(rows);
  return {
    suggested: rows.filter((r) => r.status === "suggested").length,
    unmatched: rows.filter((r) => r.status === "unmatched").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
    accepted: rows.filter((r) => r.status === "accepted").length,
    leftover: leftover.length,
    canImportRemaining: leftover.some((r) => r.status === "suggested" && canParkImportRow(r)),
  };
}
